/**
 * What `import ... from 'react-native'` resolves to in the web build.
 *
 * Everything react-native-web implements passes through untouched. The
 * exports below are the ones it either does not ship (PermissionsAndroid) or
 * ships as do-nothing stubs that this app genuinely leans on - Alert asks
 * questions whose answers delete things, BackHandler is how every sheet
 * closes on Android. Each override is the smallest browser-honest version of
 * the native behaviour, not a new feature.
 *
 * Explicit named exports win over `export *` by ESM rules, which is what
 * makes overriding this cheap.
 */

export * from 'react-native-web';

import {
  Animated as WebAnimated,
  Linking as WebLinking,
  Dimensions as WebDimensions,
  useWindowDimensions as useWebWindowDimensions,
} from 'react-native-web';

/* ------------------------------------------------------------------------- *
 * Animated - `useNativeDriver: true` stripped.
 *
 * There is no native driver in a browser. react-native-web warns and claims
 * to fall back to the JS driver, but the fallback leaves animated styles
 * frozen at their starting value - LocationScreen's address sheet animates
 * `opacity` from 0 on every stage change and stayed at 0: present,
 * clickable, invisible. Rewriting the flag routes every animation down the
 * JS path that actually writes styles. App code keeps `useNativeDriver:
 * true` untouched, which is correct on the phone.
 * ------------------------------------------------------------------------- */

type AnimatedConfig = Record<string, unknown>;

const jsDriven = (config: AnimatedConfig): AnimatedConfig => ({
  ...config,
  useNativeDriver: false,
});

/*
 * The safety flush.
 *
 * On the web, a `timing` interleaved with a branch swap can leave the
 * incoming Animated.View frozen at the START value - LocationScreen's
 * address sheet cross-fades `opacity` from 0 on every stage change and
 * arrived stuck at 0: laid out, clickable, invisible. The distilled
 * patterns (event-started timing, interrupted timing, late-mounting view)
 * all pass in isolation, so rather than chase the exact interleaving, every
 * timing gets a deadline: shortly after its duration elapses, the value is
 * snapped to `toValue` unless a newer animation has claimed it since. When
 * the animation worked this is a no-op; when it silently died it is the
 * difference between a screen and a blank.
 */
const latestClaim = new WeakMap<object, number>();

const withDeadline = (
  value: unknown,
  config: AnimatedConfig,
  animation: { start: (callback?: (result: unknown) => void) => void },
) => {
  const originalStart = animation.start.bind(animation);
  animation.start = (callback?: (result: unknown) => void) => {
    const key = value as object;
    const claim = (latestClaim.get(key) ?? 0) + 1;
    latestClaim.set(key, claim);

    const { toValue, duration } = config as {
      toValue?: unknown;
      duration?: unknown;
    };
    if (typeof toValue === 'number') {
      const wait = (typeof duration === 'number' ? duration : 500) + 100;
      setTimeout(() => {
        if (latestClaim.get(key) === claim) {
          (value as { setValue: (next: number) => void }).setValue(toValue);
        }
      }, wait);
    }
    originalStart(callback);
  };
  return animation;
};

export const Animated = Object.assign({}, WebAnimated, {
  timing: (value: unknown, config: AnimatedConfig) =>
    withDeadline(
      value,
      config,
      WebAnimated.timing(value as never, jsDriven(config) as never),
    ),
  spring: (value: unknown, config: AnimatedConfig) =>
    WebAnimated.spring(value as never, jsDriven(config) as never),
  decay: (value: unknown, config: AnimatedConfig) =>
    WebAnimated.decay(value as never, jsDriven(config) as never),
});

/* ------------------------------------------------------------------------- *
 * Dimensions - the width the app can actually draw in.
 *
 * Every layout in this app that does arithmetic - the type scale, the
 * onboarding art, the sheet heights - asks `useWindowDimensions`. What it
 * must hear is #root's real width, not the browser window's: today the two
 * are the same (`web/global.css` runs the app full-bleed and the app's own
 * responsive system handles wide screens), but reading the element keeps
 * the CSS the single source of truth - any future frame or column there is
 * honoured here automatically. Height stays the window's: #root is always
 * full-height.
 * ------------------------------------------------------------------------- */

type Dims = { width: number; height: number; scale: number; fontScale: number };

const frameWidth = (): number | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const width = document.getElementById('root')?.clientWidth;
  return width && width > 0 ? width : null;
};

const clampToFrame = (dims: Dims): Dims => {
  const frame = frameWidth();
  return frame && dims.width > frame ? { ...dims, width: frame } : dims;
};

export const Dimensions = {
  get: (dimension: 'window' | 'screen'): Dims => {
    const dims = WebDimensions.get(dimension) as Dims;
    return dimension === 'window' ? clampToFrame(dims) : dims;
  },
  addEventListener: (
    type: 'change',
    handler: (payload: { window: Dims; screen: Dims }) => void,
  ) =>
    WebDimensions.addEventListener(
      type,
      (payload: { window: Dims; screen: Dims }) =>
        handler({ ...payload, window: clampToFrame(payload.window) }),
    ),
};

export function useWindowDimensions(): Dims {
  return clampToFrame(useWebWindowDimensions() as Dims);
}

/* ------------------------------------------------------------------------- *
 * Alert - react-native-web's is a silent no-op, which would turn "Delete
 * this address?" into an app that neither asks nor acts. window.confirm is
 * ugly but honest: the question is asked, the answer is respected.
 * ------------------------------------------------------------------------- */

type AlertButton = {
  text?: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
};

export const Alert = {
  alert(title: string, message?: string, buttons?: AlertButton[]): void {
    const text = [title, message].filter(Boolean).join('\n\n');

    if (!buttons || buttons.length <= 1) {
      window.alert(text);
      buttons?.[0]?.onPress?.();
      return;
    }

    /*
     * Two-and-more buttons become OK/Cancel: the cancel-styled button (or the
     * first, when none is marked) is Cancel, and the LAST remaining button is
     * OK - which in this app's alerts is always the confirming action. A
     * three-button alert loses its middle option; nothing in the app has one
     * today.
     */
    const cancel = buttons.find(b => b.style === 'cancel') ?? buttons[0];
    const confirm = [...buttons].reverse().find(b => b !== cancel);

    if (window.confirm(text)) {
      confirm?.onPress?.();
    } else {
      cancel?.onPress?.();
    }
  },
};

/* ------------------------------------------------------------------------- *
 * BackHandler - wired to the browser's real Back button.
 *
 * The app closes every sheet and overlay page through
 * `BackHandler.addEventListener('hardwareBackPress', ...)`. react-native-web
 * stubs this out, which would make the browser's Back button leave the site
 * from three sheets deep. Instead: one history entry is pushed as a trap the
 * first time anything subscribes, popstate runs the handlers newest-first
 * exactly like Android does, and a handled press re-arms the trap. An
 * unhandled press lets the browser leave - which is what Android's "back
 * from the home tab exits the app" behaviour maps to.
 * ------------------------------------------------------------------------- */

type BackPressHandler = () => boolean | null | undefined;

const backHandlers: BackPressHandler[] = [];
let backTrapArmed = false;

const armBackTrap = (): void => {
  if (backTrapArmed || typeof window === 'undefined') {
    return;
  }
  backTrapArmed = true;

  window.history.pushState({ homingo: true }, '');
  window.addEventListener('popstate', () => {
    let handled = false;
    for (let i = backHandlers.length - 1; i >= 0; i -= 1) {
      if (backHandlers[i]()) {
        handled = true;
        break;
      }
    }
    if (handled) {
      window.history.pushState({ homingo: true }, '');
    } else {
      window.history.back();
    }
  });
};

const removeBackHandler = (handler: BackPressHandler): void => {
  const index = backHandlers.indexOf(handler);
  if (index >= 0) {
    backHandlers.splice(index, 1);
  }
};

export const BackHandler = {
  addEventListener(
    _event: 'hardwareBackPress',
    handler: BackPressHandler,
  ): { remove: () => void } {
    armBackTrap();
    backHandlers.push(handler);
    return { remove: () => removeBackHandler(handler) };
  },

  /** The pre-0.65 API, kept because it costs two lines to keep. */
  removeEventListener(
    _event: 'hardwareBackPress',
    handler: BackPressHandler,
  ): void {
    removeBackHandler(handler);
  },

  exitApp(): void {
    window.history.back();
  },
};

/* ------------------------------------------------------------------------- *
 * PermissionsAndroid - react-native-web does not export it at all, and the
 * app imports it in seven files. Every call site is guarded by
 * `Platform.OS === 'android'`, so on web these never actually run; the shape
 * exists so the imports resolve, and answers "denied" rather than lying
 * about a grant if some future call site forgets the guard.
 * ------------------------------------------------------------------------- */

export const PermissionsAndroid = {
  PERMISSIONS: {
    ACCESS_FINE_LOCATION: 'android.permission.ACCESS_FINE_LOCATION',
    ACCESS_COARSE_LOCATION: 'android.permission.ACCESS_COARSE_LOCATION',
    CAMERA: 'android.permission.CAMERA',
    RECORD_AUDIO: 'android.permission.RECORD_AUDIO',
    POST_NOTIFICATIONS: 'android.permission.POST_NOTIFICATIONS',
  },
  RESULTS: {
    GRANTED: 'granted',
    DENIED: 'denied',
    NEVER_ASK_AGAIN: 'never_ask_again',
  },
  check: async (_permission: string): Promise<boolean> => false,
  request: async (_permission: string): Promise<string> => 'denied',
};

/* ------------------------------------------------------------------------- *
 * Linking - react-native-web implements openURL and friends; it has no
 * openSettings, because a browser has no settings screen a page may open.
 * The nearest honest act is telling the person where the browser keeps them.
 * ------------------------------------------------------------------------- */

export const Linking = {
  openURL: (url: string): Promise<void> => WebLinking.openURL(url),
  canOpenURL: (url: string): Promise<boolean> => WebLinking.canOpenURL(url),
  getInitialURL: (): Promise<string | null> => WebLinking.getInitialURL(),
  openSettings: async (): Promise<void> => {
    window.alert(
      "Permissions live in your browser here. Use the icon beside the address bar (or your browser's site settings) to change what Homingo may use.",
    );
  },
};
